import { useEffect, useMemo, useRef, useState } from "react";

import useCustomerDetails from "../../../data/useCustomerDetails";
import useOffers from "../../../data/useOffers";
import usePartMutations from "../../../data/usePartMutations";
import usePartsLookupMutations from "../../../data/usePartsLookupMutations";
import useDefaultCRUDHandlers from "../../../hooks/useDefaultCRUDHandlers";
import { FormatType, IEbayOffer } from "../../../models/EbayOffer";
import { IPart } from "../../../models/Part";
import useNotification from "../../notifications/useNotifications";
import ConfirmModal from "../../shared/Confirm/ConfirmModal";
import { PartProPriceButton } from "../../shared/PriceButtons";
import UnlistedTablePriceButton from "./UnlistedTablePriceButton";
import PriceFormat from "../../../utils/priceFormat";
import { useUserLookup } from "../../../hooks/useUserLookup";
import { DateTime } from "luxon";
import Tooltip from "../../shared/Tooltip";
import {
  ChartBarSquareIcon,
  LockClosedIcon,
  SparklesIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import classNames from "../../../utils/classNames";
import { PartProBlackList } from "../../../models/PartLookup";

export default function UnlistedOfferPrice({
  part,
  showButtons = true,
  hideLabel = false,
  intelligencePopover = false,
  inlineIntelligence = false,
  compact = false,
}: {
  part: IPart;
  showButtons?: boolean;
  hideLabel?: boolean;
  intelligencePopover?: boolean;
  inlineIntelligence?: boolean;
  compact?: boolean;
}) {
  const { update } = useOffers();
  const { createOffer, patchPart } = usePartMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Part Price");
  const { addNotification } = useNotification();
  const { lookupMutation } = usePartsLookupMutations(part.id);
  const { brand } = useCustomerDetails();

  const [isEditing, setIsEditing] = useState(false);
  const [partProOpen, setPartProOpen] = useState(false);
  const [partProLoading, setPartProLoading] = useState(false);

  const priceRef = useRef<HTMLInputElement | null>(null);

  const handlePriceUpdate = (price: string | number) => {
    let doublePrice = typeof price === "number" ? price : parseFloat(price);

    if (isNaN(doublePrice)) return;

    doublePrice = Math.round(doublePrice * 100) / 100;

    if (part.offers && part.offers.length > 0) {
      const updatedOffer = {
        ...part.offers[0],
      };

      if (part.offers[0].enableBestOffer && doublePrice < (part.offers[0].autoAcceptPrice || 0)) {
        addNotification({ primaryText: "Best Offer Enabled - cannot set price below auto accept price", variant: "info" });
        return;
      }

      updateOffer(updatedOffer, doublePrice);
    } else {
      updatePart(part, doublePrice);
    }
  }

  const updatePart = (part: IPart, price: number) => {
    patchPart.mutate({
      id: part.id,
      enteredPrice: price
    }, {
      ...saveHandlers,
      onSuccess: () => {
        part.enteredPrice = price;
        saveHandlers.onSuccess();
        setIsEditing(false);
      }
    });
  }

  const updateOffer = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }

    update.mutate(offer, {
      ...saveHandlers,
      onSuccess: () => {
        const existingOffer = part.offers.find((existing) => existing.id === offer.id);
        if (existingOffer) {
          updatePrice(existingOffer, price);
        }
        saveHandlers.onSuccess();
        setIsEditing(false);
      }
    });
  }

  const updatePrice = (offer: IEbayOffer, price: number) => {
    if (offer.format === FormatType.Auction) {
      offer.auctionStartPrice = price;
    } else {
      offer.buyItNowPrice = price;
    }
  }

  const handlePartProLookup = () => {
    if (part?.partsLookupRequestId) {
      setPartProOpen(false);
      return;
    }

    setPartProLoading(true);
    lookupMutation.mutate(undefined,
      {
        onSuccess: (data) => {
          if (!data.data.requestedParts || data.data.requestedParts.length === 0) {
            addNotification({ primaryText: "No Parts Found", variant: "info", });
            setPartProLoading(false);
            return;
          }

          part.suggestedPrice = data.data.requestedParts?.find((p) => p.partCategory?.id === part.category!.id)?.priceInformation.avgPrice || 0;
          part.lastPartLookupChecked = DateTime.now().toISO();

          addNotification({ primaryText: "PartPro Lookup Successful", variant: "success", });
        },
        onError: (error) => {
          console.error("PartPro lookup failed:", error);
          addNotification({ primaryText: "PartPro Lookup Failed", variant: "error", })
        }
      }
    );

    setPartProOpen(false);
  }

  //All this just to stop the mouse wheel changing the number input value...
  useEffect(() => {
    const el = priceRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (document.activeElement === el) {
        e.preventDefault();     // THIS actually blocks value changes
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });

    return () => {
      el.removeEventListener("wheel", handleWheel);
    };
  }, [isEditing]);


  const isLocal = process.env.NODE_ENV === "development";

  const { findUser, isLoading: userLookupLoading } = useUserLookup();

  const pricedBy = useMemo(() => {
    if (!part.pricedBy || userLookupLoading) return null;

    const user = findUser(part.pricedBy);
    return user?.fullname;
  }, [part])

  const pricedTooltip = useMemo(() => {
    const parts: string[] = [];
    if (pricedBy) parts.push(`Priced by ${pricedBy}`);
    if (part.pricedOn) {
      const dt = DateTime.fromISO(part.pricedOn);
      if (dt.isValid) parts.push(dt.toFormat("d LLL yyyy"));
    }
    return parts.length > 0 ? parts.join(" · ") : null;
  }, [pricedBy, part.pricedOn]);

  // derive rendering state
  const hasOffers = part.offers && part.offers.length > 0;
  const label = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? "Auction"
      : "Buy It Now"
    : `Buy It Now ${isLocal ? "**" : ""}`;
  const inputValue = hasOffers
    ? part.offers[0]?.format === FormatType.Auction
      ? part.offers[0]?.auctionStartPrice
      : part.offers[0]?.buyItNowPrice
    : part.enteredPrice;

  return (
    <>
      <ConfirmModal
        open={partProOpen}
        setOpen={setPartProOpen}
        onConfirm={handlePartProLookup}
        isLoading={partProLoading}
        title={"Activate PartPro Lookup?"}
        message={`This will start a PartPro lookup on this vehicle, this may take some time.\n
                  This will check against an exact part number match, display it for you in the table, show how many it matched on and give you an accurate price estimate for the parts.\n
                  This costs 1 token per lookup per vehicle (multiple parts from the same vehicle count as one lookup), you have ${brand.data?.data.lookupTokens ?? 0
          } tokens remaining.`}
        confirmButtonText={null}
      />

      <div
        className={classNames(
          "flex flex-col gap-y-2",
          inlineIntelligence ? "w-full min-w-0 items-stretch" : "items-center",
          compact ? "py-1 px-1" : "py-6 px-1"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {!hideLabel && <p className="text-xs text-gray-500">{label}</p>}
        <div
          onWheel={(e) => {
            e.preventDefault();
          }}
          title={pricedBy ? `Priced By: ${pricedBy}` : ""}
          className={classNames(
            inlineIntelligence ? "flex items-stretch gap-1.5 w-full min-w-0" : "flex items-center gap-1"
          )}
        >
          {isEditing ? (
            inlineIntelligence ? (
              <div className="flex items-center gap-1 rounded-lg bg-gradient-to-b from-amber-50/80 to-white px-2 py-1.5 ring-1 ring-inset ring-amber-200/60 flex-1">
                <span className="text-sm font-semibold text-amber-900">£</span>
                <input
                  ref={priceRef}
                  key={part.id}
                  defaultValue={inputValue ?? undefined}
                  step={1}
                  className="no-spinner w-full min-w-0 flex-1 bg-transparent text-base font-semibold tabular-nums text-amber-900 placeholder:text-amber-300 focus:outline-none"
                  type="number"
                  onBlur={(e) => handlePriceUpdate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                />
              </div>
            ) : (
              <>
                <span className="mr-px">£</span>
                <input
                  ref={priceRef}
                  key={part.id}
                  defaultValue={inputValue ?? undefined}
                  step={1}
                  className="no-spinner h-8 w-20 border p-1 text-sm"
                  type="number"
                  onBlur={(e) => handlePriceUpdate(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.currentTarget.blur();
                    }
                  }}
                />
              </>
            )
          ) : inlineIntelligence ? (
            <Tooltip
              content={
                pricedTooltip
                  ? `${pricedTooltip} · Click to edit`
                  : "Click to edit price"
              }
              position="top"
            >
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="w-full rounded-lg bg-gradient-to-b from-amber-50/80 to-white px-2 py-1 text-left ring-1 ring-inset ring-amber-200/60 transition hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-primary-500/30 flex-1"
              >
                {hideLabel && (
                  <div className="flex items-center justify-between gap-1">
                    <span className="text-[9px] font-semibold uppercase tracking-wide text-amber-900/80">
                      {label}
                    </span>
                    {pricedBy && (
                      <span
                        aria-hidden
                        className="text-[9px] font-medium text-amber-900/60"
                      >
                        {pricedBy.split(" ")[0]}
                      </span>
                    )}
                  </div>
                )}
                <div
                  className={classNames(
                    "text-base font-semibold tabular-nums leading-tight text-amber-900",
                    inputValue == null && "text-slate-400"
                  )}
                >
                  {inputValue != null ? PriceFormat(inputValue) : "—"}
                </div>
              </button>
            </Tooltip>
          ) : (
            <div
              className="h-8 w-20 border p-1 text-sm flex hover:bg-gray-200 hover:scale-110 items-center justify-center cursor-pointer transition-all rounded"
              onClick={() => setIsEditing(true)}
            >
              {inputValue != null ? PriceFormat(inputValue) : "-"}
            </div>
          )}
          {intelligencePopover && !inlineIntelligence && (
            <PricingIntelligenceButton
              part={part}
              onPriceSelect={handlePriceUpdate}
              partProLoading={partProLoading}
              onLockedClick={() => setPartProOpen(true)}
            />
          )}
        </div>

        {inlineIntelligence && (
          <InlinePricingIntelligence
            part={part}
            onPriceSelect={handlePriceUpdate}
            partProLoading={partProLoading}
            onLockedClick={() => setPartProOpen(true)}
          />
        )}

        {showButtons && !intelligencePopover && !inlineIntelligence && (
          <div className="flex flex-col space-y-1 pl-1">
            <Tooltip content="Part Pro Price">
              <PartProPriceButton
                part={part}
                onPriceSelect={handlePriceUpdate}
                partProUnlocked={
                  part.partsLookupRequestId != null ||
                  part.lastPartLookupChecked != null
                }
                partProLoading={partProLoading}
                onLockedClick={() => setPartProOpen(true)}
              />
            </Tooltip>

            <Tooltip content="Market Data Price">
              <UnlistedTablePriceButton
                price={part.marketDataPrice}
                onPriceSelect={handlePriceUpdate}
                colour="bg-[#9b0071]"
                title="Market Data Price"
              />
            </Tooltip>

            <Tooltip content="Last Sold Price">
              <UnlistedTablePriceButton
                price={part.lastSoldPrice}
                onPriceSelect={handlePriceUpdate}
                title="Last Sold Price"
              />
            </Tooltip>
          </div>
        )}
      </div>
    </>
  );
}

function InlinePricingIntelligence({
  part,
  onPriceSelect,
  partProLoading,
  onLockedClick,
}: {
  part: IPart;
  onPriceSelect: (price: number) => void;
  partProLoading: boolean;
  onLockedClick: () => void;
}) {
  const partProUnlocked =
    part.partsLookupRequestId != null || part.lastPartLookupChecked != null;
  const isPartProBlacklisted = PartProBlackList.includes(
    part.vehicle?.combinedMake?.toLowerCase() ?? ""
  );
  const partProUcid = (part.category?.ucid ?? "").trim();
  const showPartProRow = !!partProUcid && !isPartProBlacklisted;
  const partProPrice = part.suggestedPrice
    ? parseFloat(Number(part.suggestedPrice).toFixed(2))
    : null;

  const marketPrice = part.marketDataPrice ?? null;
  const lastSoldPrice = part.lastSoldPrice ?? null;

  const rows: {
    label: string;
    short: string;
    price: number | null;
    accent: keyof typeof ACCENTS;
    locked?: boolean;
    loading?: boolean;
    onClick: () => void;
  }[] = [
    {
      label: "Market Data Price",
      short: "Market",
      price: marketPrice,
      accent: "violet",
      onClick: () => {
        if (marketPrice != null && marketPrice > 0) onPriceSelect(marketPrice);
      },
    },
    ...(showPartProRow
      ? [
          {
            label: partProUnlocked
              ? "Part Number Price (PartPro)"
              : "Activate PartPro to look up an exact part-number price",
            short: "PartPro",
            price: partProPrice,
            accent: "emerald" as keyof typeof ACCENTS,
            locked: !partProUnlocked,
            loading: partProLoading,
            onClick: () => {
              if (!partProUnlocked) {
                onLockedClick();
                return;
              }
              if (partProPrice != null && partProPrice > 0)
                onPriceSelect(partProPrice);
            },
          },
        ]
      : []),
    {
      label: "Last Sold Price",
      short: "Last Sold",
      price: lastSoldPrice,
      accent: "sky",
      onClick: () => {
        if (lastSoldPrice != null && lastSoldPrice > 0) onPriceSelect(lastSoldPrice);
      },
    },
  ];

  const gridCols = rows.length >= 3 ? "grid-cols-3" : "grid-cols-2";

  return (
    <div
      className={classNames("mt-1 grid w-full gap-1.5", gridCols)}
      onClick={(e) => e.stopPropagation()}
    >
      {rows.map((row) => {
        const tone = TILE_TONES[row.accent];
        const hasPrice = row.price != null && row.price > 0;
        const clickable = row.locked || hasPrice;
        return (
          <button
            key={row.short}
            type="button"
            onClick={row.onClick}
            disabled={!clickable || row.loading}
            title={
              row.locked
                ? "Click to unlock"
                : hasPrice
                ? `Click to apply ${PriceFormat(row.price!)}`
                : "No data available"
            }
            className={classNames(
              "rounded-lg bg-gradient-to-b px-1.5 py-1.5 text-left ring-1 ring-inset transition",
              tone,
              clickable
                ? "cursor-pointer hover:brightness-105 focus:outline-none focus:ring-2 focus:ring-primary-500/30"
                : "cursor-default opacity-60",
              row.loading && "animate-pulse"
            )}
          >
            <div className="text-[9px] font-semibold uppercase tracking-wide opacity-80">
              {row.short}
            </div>
            <div
              className={classNames(
                "flex items-center gap-1 text-base font-semibold tabular-nums leading-tight",
                !hasPrice && !row.locked && "text-slate-400"
              )}
            >
              {row.locked ? (
                <>
                  <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="text-xs font-semibold">Unlock</span>
                </>
              ) : hasPrice ? (
                PriceFormat(row.price!)
              ) : (
                "—"
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function PricingIntelligenceButton({
  part,
  onPriceSelect,
  partProLoading,
  onLockedClick,
}: {
  part: IPart;
  onPriceSelect: (price: number) => void;
  partProLoading: boolean;
  onLockedClick: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const partProUnlocked =
    part.partsLookupRequestId != null || part.lastPartLookupChecked != null;
  const isPartProBlacklisted = PartProBlackList.includes(
    part.vehicle?.combinedMake?.toLowerCase() ?? ""
  );
  const partProUcid = (part.category?.ucid ?? "").trim();
  const showPartProRow = !!partProUcid && !isPartProBlacklisted;
  const partProPrice = part.suggestedPrice
    ? parseFloat(Number(part.suggestedPrice).toFixed(2))
    : null;

  const marketPrice = part.marketDataPrice ?? null;
  const lastSoldPrice = part.lastSoldPrice ?? null;

  const hasAny =
    showPartProRow ||
    (marketPrice != null && marketPrice > 0) ||
    (lastSoldPrice != null && lastSoldPrice > 0);

  return (
    <div ref={ref} className="relative inline-block">
      <Tooltip content="Pricing intelligence">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          aria-label="Pricing intelligence"
          aria-expanded={open}
          className={classNames(
            "inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-400",
            hasAny
              ? "bg-violet-100 text-violet-700 ring-1 ring-inset ring-violet-200 hover:bg-violet-200"
              : "bg-slate-100 text-slate-400 ring-1 ring-inset ring-slate-200 hover:bg-slate-200"
          )}
        >
          <SparklesIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </Tooltip>

      {open && (
        <div
          role="dialog"
          aria-label="Pricing intelligence"
          onClick={(e) => e.stopPropagation()}
          className="absolute right-0 top-full z-50 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-3 text-left shadow-xl"
        >
          <div className="mb-2 flex items-center justify-between">
            <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <ChartBarSquareIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Pricing Intelligence
            </p>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close pricing intelligence"
              className="inline-flex h-6 w-6 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
            >
              <XMarkIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <ul className="space-y-1.5">
            <PricingIntelligenceRow
              label="Market Data Price"
              description="Average market value from listing aggregators"
              price={marketPrice}
              accent="violet"
              onApply={() => {
                if (marketPrice != null && marketPrice > 0) {
                  onPriceSelect(marketPrice);
                  setOpen(false);
                }
              }}
            />

            {showPartProRow && (
              <PricingIntelligenceRow
                label="Part Number Price (PartPro)"
                description={
                  partProUnlocked
                    ? "Average price for matched OEM part numbers"
                    : "Activate PartPro to look up an exact part-number price"
                }
                price={partProPrice}
                accent="emerald"
                locked={!partProUnlocked}
                loading={partProLoading}
                onApply={() => {
                  if (!partProUnlocked) {
                    onLockedClick();
                    setOpen(false);
                    return;
                  }
                  if (partProPrice != null && partProPrice > 0) {
                    onPriceSelect(partProPrice);
                    setOpen(false);
                  }
                }}
              />
            )}

            <PricingIntelligenceRow
              label="Last Sold Price"
              description="Most recent sale price for this part"
              price={lastSoldPrice}
              accent="sky"
              onApply={() => {
                if (lastSoldPrice != null && lastSoldPrice > 0) {
                  onPriceSelect(lastSoldPrice);
                  setOpen(false);
                }
              }}
            />
          </ul>

          {!hasAny && (
            <p className="mt-2 text-center text-xs text-slate-400">
              No pricing intelligence available for this part yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

const ACCENTS: Record<string, { ring: string; bg: string; text: string }> = {
  violet: { ring: "ring-violet-200", bg: "bg-violet-50", text: "text-violet-700" },
  emerald: { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-700" },
  sky: { ring: "ring-sky-200", bg: "bg-sky-50", text: "text-sky-700" },
};

// Gradient tile tones matching the 30/60/90 sold tiles in the stock summary.
const TILE_TONES: Record<string, string> = {
  violet: "from-violet-50/80 to-white ring-violet-200/50 text-violet-900",
  emerald: "from-emerald-50/80 to-white ring-emerald-200/50 text-emerald-900",
  sky: "from-sky-50/80 to-white ring-sky-200/50 text-sky-900",
};

function PricingIntelligenceRow({
  label,
  description,
  price,
  accent,
  locked = false,
  loading = false,
  onApply,
}: {
  label: string;
  description: string;
  price: number | null;
  accent: keyof typeof ACCENTS;
  locked?: boolean;
  loading?: boolean;
  onApply: () => void;
}) {
  const acc = ACCENTS[accent];
  const hasPrice = price != null && price > 0;
  const clickable = locked || hasPrice;

  return (
    <li>
      <button
        type="button"
        disabled={!clickable || loading}
        onClick={onApply}
        title={
          locked
            ? "Click to unlock"
            : hasPrice
            ? `Click to apply ${PriceFormat(price!)}`
            : "No data available"
        }
        className={classNames(
          "flex w-full items-center gap-2 rounded-md p-2 text-left transition-colors",
          clickable
            ? `hover:${acc.bg} cursor-pointer`
            : "cursor-default opacity-60",
          loading && "animate-pulse"
        )}
      >
        <span
          className={classNames(
            "inline-flex h-7 w-16 flex-none items-center justify-center rounded-md text-xs font-bold ring-1 ring-inset",
            acc.ring,
            acc.bg,
            acc.text
          )}
        >
          {locked ? (
            <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
          ) : hasPrice ? (
            PriceFormat(price!)
          ) : (
            "—"
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-semibold text-slate-800">
            {label}
          </span>
          <span className="block text-[11px] leading-snug text-slate-500">
            {description}
          </span>
        </span>
      </button>
    </li>
  );
}


//Old display
// <span>
//   {
//     info.getValue()?.at(0)?.format === FormatType.Auction ?
//       PriceFormat(info.getValue()?.at(0)?.auctionStartPrice) :
//       PriceFormat(info.getValue()?.at(0)?.buyItNowPrice)
//   }
// </span>,